import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useOpenRecord, usePanePrefs } from '@/design';

/** Spread on a row's name link, or on what holds it (`{...ROW_NAME}`): its plain click follows
 *  *Opens in*. */
export const ROW_NAME = { 'data-row-name': '' } as const;

/** The links a table's name clicks are caught on by default: a link marked `ROW_NAME`, or one
 *  inside an element that is. */
const NAME_LINKS = 'a[data-row-name], [data-row-name] a';

/**
 * A work page's table under *Opens in* (SR-530) — the one place a plain click on a row's name (its
 * record's link) follows the reader's choice, as the row's own press does: a new tab; or beside the
 * list as a sheet over this page (`useOpenRecord`), the list's search kept for the record's way
 * back; or, beside the list as its column, the link's own press. Ctrl/⌘, Shift or the middle button
 * keep the link's way. `selector` names the links caught (else `ROW_NAME`'s); `open`, where the page
 * opens its records itself (another area's list, `useListOrigin`), opens one beside the list.
 */
export function RowNameClicks({
  selector,
  accept,
  open,
  off = false,
  children
}: {
  selector?: string;
  /** Which of the links found are a row's record's — any link, with `accept`, where the table marks
   *  none (another area's list, the SEC ADV register's names). */
  accept?: (link: HTMLAnchorElement) => boolean;
  open?: (to: string) => void;
  /** Catches nothing — a table drawn with no list (a record's own work items). */
  off?: boolean;
  children: React.ReactNode;
}) {
  const prefs = usePanePrefs();
  const openRecord = useOpenRecord();
  const location = useLocation();
  const onClickCapture = (event: React.MouseEvent) => {
    if (off || event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(selector ?? (accept ? 'a[href]' : NAME_LINKS));
    if (!link || link.target === '_blank' || link.hasAttribute('data-new-tab')) return;
    if (accept && !accept(link)) return;
    const to = `${link.pathname}${link.search}`;
    if (prefs.opens === 'tab') {
      event.preventDefault();
      window.open(link.href, '_blank', 'noopener');
    } else if (open) {
      event.preventDefault();
      open(to);
    } else if (prefs.recordLayout === 'sheet') {
      event.preventDefault();
      openRecord(to, { search: location.search, list: true });
    }
  };
  return (
    <div className="contents" onClickCapture={onClickCapture}>
      {children}
    </div>
  );
}
